import qs from 'query-string';
import type { Location } from 'react-router';

import { parseQueryString } from '../../services/utils';
import type { Jurisdiction } from '../data/jurisdictions';
import type { ActiveLocale } from '../utils/localization';
import type { Option } from '../utils/option';

import type { GoogleAnalyticsLegacyEvent } from './analytics-event';

declare global {
  interface Window {
    dataLayer: any[];
  }
}

export type UTMFields = {
  partnerId: string;
  partnerCampaign: string;
  partnerContent: string;
  partnerCustom: string;
  partnerMedium: string;
  partnerTerm: string;
  appIntent?: string;
};

export interface AnalyticsAdapter {
  routeLoaded(
    location: Location,
    locale: ActiveLocale,
    jurisdiction: Option<Jurisdiction>
  ): void;
  recordEvent(event: GoogleAnalyticsLegacyEvent): Promise<void>;
}

type EventOptions = {
  category: string;
  action: string;
  label?: string;
};

type ClickedEventOptions = {
  category: string;
  target: string;
  isExternal: boolean;
};

/**
 * Service to send web analytics to a provider of our choice.
 *
 * Uses the adapter pattern so that it can have helper methods (like
 * {@link #clicked}) that don’t have to be re-implemented in each adaptor. But
 * overall doesn’t need to be too general because we really only have one place
 * we send analytics.
 */
export class AnalyticsService {
  constructor(private adapter: AnalyticsAdapter) {}

  /**
   * We send a specific `route_loaded` event rather than relying on history
   * change events. This event gets sent the first render after a route change,
   * so it has updated lang and jurisdiction values (in case those were
   * calculated in the loader).
   *
   * @param location
   * @param locale
   * @param jurisdiction
   */
  public routeLoaded(
    location: Location,
    locale: ActiveLocale,
    jurisdiction: Option<Jurisdiction>
  ) {
    this.adapter.routeLoaded(location, locale, jurisdiction);
  }

  public event({ category, action, label }: EventOptions): Promise<void> {
    return this.adapter.recordEvent({
      eventCategory: category,
      eventAction: action,
      ...(label ? { eventLabel: label } : {}),
    });
  }

  public redirected({ fromPath }: { fromPath: string }) {
    return this.adapter.recordEvent({
      eventCategory: 'Shortcode Redirect',
      eventAction: 'redirect',
      eventLabel: fromPath,
    });
  }

  public clicked({
    category,
    target,
    isExternal,
  }: ClickedEventOptions): Promise<void> {
    if (isExternal) {
      return this.adapter.recordEvent({
        eventCategory: 'Outbound Link',
        eventAction: 'click',
        eventLabel: target,
      });
    } else {
      return this.adapter.recordEvent({
        eventCategory: category,
        eventAction: 'click',
        eventLabel: target,
      });
    }
  }
}

/**
 * Implementation for tests that just swallows the events and doesn’t do
 * anything with them.
 *
 * We include the parameters so that subclasses can override this with
 * partial implementations.
 */
export class NullAnalyticsAdapter implements AnalyticsAdapter {
  routeLoaded(_: Location) {}

  recordEvent(_: GoogleAnalyticsLegacyEvent) {
    return Promise.resolve();
  }
}

/**
 * Default adaptor that sends information to Google Tag Manager (where it makes
 * its way to Google Analytics).
 */
export class GoogleTagManagerAdapter implements AnalyticsAdapter {
  /**
   * Sends the given data into the GTM data layer array.
   *
   * Returns a Promise. If the data contains an `event` property, the promise
   * will resolve after GTM processes the event. Otherwise the Promise will
   * resolve immediately.
   */
  private dataLayerPush(data: Record<string, string | number>): Promise<void> {
    return new Promise((resolve) => {
      if ('event' in data) {
        window.dataLayer.push({
          ...data,
          eventCallback: () => resolve(),
        });
      } else {
        window.dataLayer.push(data);
        resolve();
      }
    });
  }

  public routeLoaded(
    location: Location<unknown>,
    locale: ActiveLocale,
    jurisdiction: Option<Jurisdiction>
  ) {
    const params = parseQueryString(location.search);

    // These get turned into the `language` and `jurisdiction` variables, so we
    // don’t need them in the URL. This keeps GA from separating pages
    // needlessly.
    delete params['state'];
    delete params['lang'];

    let routeUrl = location.pathname;

    if (Object.keys(params).length > 0) {
      routeUrl = routeUrl + '?' + qs.stringify(params);
    }

    this.dataLayerPush({
      event: 'route_loaded',
      routeUrl: routeUrl,

      language: locale,
      jurisdiction: jurisdiction ?? '[unknown]',
    });
  }

  public recordEvent(event: GoogleAnalyticsLegacyEvent): Promise<void> {
    return this.dataLayerPush({
      event: 'legacy_event',
      ...event,
    });
  }
}
